import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  CSSProperties,
} from "react";
import { useAtom } from "jotai";
import { useParams } from "react-router-dom";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
import { ErrorBoundary } from "react-error-boundary";

import DashboardToolbar from "./DashboardToolbar";
import {
  handleWidgetDefaults,
  typeUsesChartSettings,
  getNewWidgetLayouts,
} from "../../../utils/DashboardUtils";
import { editDashboardAtom } from "../../../atoms/ViewAtoms";
import useUpdateView from "../../../api/api-hooks/views/useUpdateView";
import { Page } from "../../../types/GlobalInterfaces";
import { View } from "../../../types/PageInterfaces";
import ViewWidgetDrawer from "../../Drawers/ViewWidgetDrawer";
import TableWidgetSettings from "../../Widgets/WidgetSettingsDrawers/TableWidgetSettings";
import ChartSettings from "../../Widgets/WidgetSettingsDrawers/ChartSettings";
import DocumentWidgetSettings from "../../Widgets/WidgetSettingsDrawers/DocumentWidgetSettings";
import HeaderWidgetSettings from "../../Widgets/WidgetSettingsDrawers/HeaderWidgetSettings";
import AppEmptyState from "../../../elements/AppEmptyState";
import WidgetRenderer from "../../Widgets/WidgetRenderer";
import { WidgetType } from "../../../types/DashboardInterfaces";
import StrUtils from "../../../utils/StrUtils";
import { SelectedChart } from "../../Widgets/WidgetTypes";
import FlowWidgetSettings from "../../Widgets/WidgetSettingsDrawers/FlowWidgetSettings";
import DashboardCardToolbar from "./DashboardCardToolbar";
import TenantAPI from "../../../api/api/TenantAPI";
import MapSettings from "../../Widgets/WidgetSettingsDrawers/MapSettings";
import { CrashViewComponent } from "../../../views/other/CrashView";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface Props {
  page: Page;
  view: View;
  parentViewID: string | undefined;
  record: Record<string, any> | undefined;
  insightsModule?: string;
  topLevelFilters?: any;
  onSelectGroup?: (chartGroup: SelectedChart[]) => void;
  selectedGroup?: SelectedChart[];
}

/**
 * PageViewDashboard - Fully responsive dashboard builder built with React Grid Layout and Recharts.
 */
const PageViewDashboard = ({
  page,
  view,
  parentViewID,
  record,
  insightsModule,
  topLevelFilters,
  onSelectGroup,
  selectedGroup,
}: Props) => {
  const { pageID, workspaceID }: any = useParams();
  const [editMode, setEditMode] = useAtom(editDashboardAtom);
  const { updateView, viewUpdating } = useUpdateView(pageID);

  const [layouts, setLayouts] = useState<Layouts>(
    (view?.Layouts as Layouts) ?? { lg: [], md: [], sm: [], xs: [], xxs: [] }
  );
  const [sections, setSections] = useState(view?.Sections ?? []);
  const [viewWidget, setViewWidget] = useState(null);
  const [settingsSection, setSettingsSection] = useState<any>(null);
  const [openAddWidget, setOpenAddWidget] = useState(false); // For open the Add Widget button in DasboardToolbar.
  const [widgetOpen, setWidgetOpen] = useState(false); // Whether or not a widget is being viewed (ViewWidgetDrawer)
  const [rendered, setRendered] = useState(false);

  // Refs
  const pageViewDashboardRef = useRef<any>();
  const layoutsRef = useRef<any>();
  const sectionsRef = useRef<any>();
  layoutsRef.current = layouts;
  sectionsRef.current = sections;

  const isEmpty = Array.isArray(sections) && sections?.length === 0;

  const closeSettingsDrawer = () => {
    setSettingsSection(null);
  };

  const handleLayoutChange = (_: Layout[], allLayouts: Layouts) => {
    const payload = { Layouts: allLayouts, Sections: sections };

    if (JSON.stringify(allLayouts) !== JSON.stringify(view?.Layouts)) {
      setLayouts(allLayouts);
      updateView({ viewID: view.id, payload, parentViewID });
    }
  };

  const handleLayoutChangeRef = useRef<any>();
  handleLayoutChangeRef.current = handleLayoutChange;

  const handleOpenWidget = useCallback((widget: any) => {
    setViewWidget(widget);
    setWidgetOpen(true);
  }, []);

  const addWidget = (type: WidgetType, label: string) => {
    const widgetID = StrUtils.uuidv4();

    const generatedLayout = getNewWidgetLayouts(widgetID, type);
    const newLayouts = {
      lg: layouts?.lg
        ? [...layouts.lg, generatedLayout.lg]
        : [generatedLayout.lg],
      md: layouts?.md
        ? [...layouts.md, generatedLayout.md]
        : [generatedLayout.md],
      sm: layouts?.sm
        ? [...layouts.sm, generatedLayout.sm]
        : [generatedLayout.sm],
      xs: layouts?.xs
        ? [...layouts.xs, generatedLayout.xs]
        : [generatedLayout.xs],
      xxs: layouts?.xxs
        ? [...layouts.xxs, generatedLayout.xxs]
        : [generatedLayout.xxs],
    };

    const newSectionItem = {
      id: widgetID,
      Type: type,
      Name: `New ${label}`,
      Description: "",
    };

    handleWidgetDefaults(newSectionItem);

    const newSections = [...sections, newSectionItem];

    const payload = { Layouts: newLayouts, Sections: newSections };

    setLayouts(newLayouts as Layouts);
    setSections(newSections);
    updateView({ viewID: view.id, payload, parentViewID });
  };

  const deleteWidget = useCallback(
    async (sectionID: string) => {
      const newLayouts: Layouts = {};
      let newSections;

      // Get the section to delete, so we can check it's Type, and perform any cleanup actions.
      const section = sectionsRef.current?.find(
        (s: any) => s?.id === sectionID
      );

      // Ensure Flow Chart record is deleted, when deleting a Flow Chart Widget.
      if (section?.Type === "FlowChart" && section?.RecordID) {
        try {
          await TenantAPI.deleteRecord("Flows", section.RecordID);
        } catch {}
      }

      newSections = sectionsRef.current.filter((s: any) => s.id !== sectionID);

      for (const key of Object.keys(layoutsRef.current)) {
        newLayouts[key] = layoutsRef.current[key].filter(
          (l: any) => l.i !== sectionID
        );
      }

      setSections(newSections);
      setLayouts(newLayouts);

      updateView({
        viewID: view.id,
        payload: {
          Layouts: newLayouts,
          Sections: newSections,
        },
        parentViewID,
      });
    },
    [view.id, updateView]
  );

  const updateWidget = useCallback(
    (newSection: any, onSuccess: any) => {
      const editedDashboardViewActivity = {
        id: StrUtils.uuidv4(),
        Type: "edited-dashboard-view",
        Page: {
          id: page.id,
          Name: page.Name,
          Logo: page.Icon,
        },
        View: {
          id: view.id,
          Name: view.Name,
          Type: view.Type,
        },
        Widget: {
          id: newSection.id,
          Name: newSection.Name,
          Type: newSection.Type,
        },
      };

      setSections((prev) => {
        const newSections = prev.map((s) => {
          if (s.id === newSection.id) {
            return newSection;
          }
          return s;
        });

        // handleLockedWidth(
        //   newSection.id,
        //   newSection?.LockWidth,
        //   layoutsRef.current,
        //   setUpdate,
        //   handleLayoutChangeRef.current
        // );

        // handleLockedHeight(
        //   newSection.id,
        //   newSection?.LockHeight,
        //   layoutsRef.current,
        //   setUpdate,
        //   handleLayoutChangeRef.current
        // );

        updateView({
          viewID: view.id,
          payload: { Sections: newSections },
          userActivity: {
            WorkspaceID: workspaceID,
            Activity: editedDashboardViewActivity,
          },
          onSuccess: onSuccess,
          parentViewID,
        });

        return newSections;
      });
    },
    [view.id, updateView]
  );

  const gridChildren = useMemo(() => {
    return sections.map((section) => {
      const cardStyles: CSSProperties = {
        width: "100%",
        height: "calc(100% - 40px)",
        overflow: "auto",
      };
      let cardClass = "";

      if (section?.Type === "Table") {
        cardStyles.overflow = section.Type === "Table" ? "scroll" : "hidden";
        cardClass = "pb-0";
      } else if (section?.Type === "Header") {
        cardStyles.height = "100%";
      } else if (section?.Type === "Document") {
        cardClass = "px-2";
      } else if (section?.Type === "FlowChart") {
        cardStyles.height = "100%";
        cardClass = "px-3";
      } else {
        cardClass = "px-3 pb-3";
      }

      return (
        <div
          key={section.id}
          className="bg-white"
          style={{
            borderRadius: editMode ? 7 : 20,
            overflow: "hidden",
            borderColor: "#d8deda",
            boxShadow: "0px 1px 4px 3px rgba(132, 133, 132, 0.3)",
          }}
        >
          <DashboardCardToolbar
            section={section}
            onDelete={deleteWidget}
            onUpdate={updateWidget}
            onOpen={() => handleOpenWidget(section)}
            editMode={editMode}
            setSettings={setSettingsSection}
            viewID={view?._id}
            recordID={record?._id}
          />
          <div className={cardClass} style={cardStyles}>
            <WidgetRenderer
              section={section}
              setSettings={setSettingsSection}
              updateWidget={updateWidget}
              editMode={editMode}
              deleteWidget={deleteWidget}
              record={record}
              theme={view?.Theme}
              topLevelFilters={topLevelFilters}
              onSelectGroup={onSelectGroup}
              selectedGroup={selectedGroup}
              view={view}
              pageID={pageID || page?.id}
              parentViewID={parentViewID}
            />
          </div>
        </div>
      );
    });
  }, [
    sections,
    deleteWidget,
    updateWidget,
    handleOpenWidget,
    editMode,
    view,
    topLevelFilters,
    selectedGroup,
    record,
    parentViewID,
    pageID,
  ]);

  // Prevents animations on first render.
  useEffect(() => {
    const timer = setTimeout(() => setRendered(true), 1);
    return () => clearTimeout(timer);
  }, []);
  return (
    <>
      {/* TODO: Custom Breakpoint Selection while editor a dashboard, so user can easily configure for all screen sizes. */}
      {/* <h3 className="mb-0">Breakpoint: {breakpoint}</h3> */}
      <DashboardToolbar
        addWidget={addWidget}
        editMode={editMode}
        openAddWidget={openAddWidget}
        setOpenAddWidget={setOpenAddWidget}
        insightsEnabled={!!insightsModule}
      />
      <ErrorBoundary FallbackComponent={CrashViewComponent}>
        <div
          ref={pageViewDashboardRef}
          className="mx--2 w-auto"
          style={{
            overflow: "hidden",
            minHeight: insightsModule ? undefined : "100vh",
          }}
        >
          <ResponsiveGridLayout
            measureBeforeMount={rendered} // Only works as expected on RGL 1.3.x... LOL
            className={rendered ? "layout animated-gl" : undefined}
            onLayoutChange={handleLayoutChange}
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 }}
            rowHeight={75}
            compactType="vertical"
            draggableHandle=".rgl-drag-handle"
            isDraggable={editMode}
            isResizable={editMode}
          >
            {gridChildren}
          </ResponsiveGridLayout>
          {isEmpty && !editMode && (
            <AppEmptyState
              title="Create your first Widget"
              description={`This view doesn't have any widgets added yet. Click "Edit Page" to get started with your dashboard.`}
              buttonTitle="Add Widget"
              className="mb-4"
              onClick={() => {
                setEditMode(true);
                setOpenAddWidget(true);
              }}
            />
          )}
        </div>
      </ErrorBoundary>
      <ViewWidgetDrawer
        open={widgetOpen}
        onClose={() => setWidgetOpen(false)}
        section={viewWidget}
        theme={view?.Theme}
        record={record}
        view={view}
        pageID={pageID || page?.id}
        parentViewID={parentViewID}
        topLevelFilters={topLevelFilters}
        setSettings={setSettingsSection}
      />
      <TableWidgetSettings
        open={settingsSection?.Type === "Table"}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        theme={view?.Theme}
      />
      <ChartSettings
        open={typeUsesChartSettings(settingsSection?.Type)}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        isUpdating={viewUpdating}
        record={record}
        theme={view?.Theme}
        insightsModule={insightsModule}
        topLevelFilters={topLevelFilters}
      />
      <MapSettings
        open={settingsSection?.Type === "Map"}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        theme={view?.Theme}
        insightsModule={insightsModule}
        isUpdating={viewUpdating}
        record={record}
        topLevelFilters={topLevelFilters}
      />
      <DocumentWidgetSettings
        open={settingsSection?.Type === "Document"}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        theme={view?.Theme}
      />
      <HeaderWidgetSettings
        open={settingsSection?.Type === "Header"}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        isUpdating={viewUpdating}
        theme={view?.Theme}
        record={record}
      />
      <FlowWidgetSettings
        open={settingsSection?.Type === "FlowChart"}
        onClose={closeSettingsDrawer}
        setSettings={setSettingsSection}
        section={settingsSection}
        updateWidget={updateWidget}
        isUpdating={viewUpdating}
        insightsModule={insightsModule}
        pageID={page?._id!}
        viewID={view?._id!}
        isSubview={!!parentViewID}
      />
    </>
  );
};

export default PageViewDashboard;
